import { useState, useEffect, useRef } from 'react'

interface DateInputProps {
  value: string // yyyy-mm-dd
  onChange: (value: string) => void
  className?: string
  required?: boolean
  min?: string // yyyy-mm-dd
  max?: string // yyyy-mm-dd
  placeholder?: string
}

/** Convert yyyy-mm-dd to dd/mm/yyyy */
function toDisplay(iso: string): string {
  if (!iso) return ''
  const [y, m, d] = iso.split('-')
  if (!y || !m || !d) return iso
  return `${d}/${m}/${y}`
}

/** Convert dd/mm/yyyy to yyyy-mm-dd */
function toIso(display: string): string {
  if (!display) return ''
  const [d, m, y] = display.split('/')
  if (!d || !m || !y) return display
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
}

export default function DateInput({ value, onChange, className = '', required, min, max, placeholder }: DateInputProps) {
  const [display, setDisplay] = useState(() => toDisplay(value))
  const hiddenRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setDisplay(toDisplay(value))
  }, [value])

  const handleTextChange = (text: string) => {
    // Allow only digits and slashes
    const cleaned = text.replace(/[^\d/]/g, '')

    // Auto-insert slashes
    let formatted = cleaned
    if (cleaned.length === 2 && !cleaned.includes('/')) {
      formatted = cleaned + '/'
    } else if (cleaned.length === 5 && cleaned.indexOf('/') === 2 && cleaned.lastIndexOf('/') === 2) {
      formatted = cleaned + '/'
    }

    setDisplay(formatted)

    // If complete date dd/mm/yyyy, convert and emit
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(formatted)) {
      const iso = toIso(formatted)
      const date = new Date(iso + 'T00:00:00')
      if (!isNaN(date.getTime())) {
        if ((min && iso < min) || (max && iso > max)) return
        onChange(iso)
      }
    }
  }

  const handleBlur = () => {
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(display)) {
      setDisplay(toDisplay(value))
    }
  }

  const handleCalendarClick = () => {
    if (hiddenRef.current) {
      hiddenRef.current.showPicker()
    }
  }

  const handlePickerChange = (isoValue: string) => {
    if (isoValue) {
      setDisplay(toDisplay(isoValue))
      onChange(isoValue)
    }
  }

  return (
    <div className="relative inline-flex">
      <input
        type="text"
        value={display}
        onChange={(e) => handleTextChange(e.target.value)}
        onBlur={handleBlur}
        className={className}
        placeholder={placeholder || 'dd/mm/aaaa'}
        maxLength={10}
        required={required}
      />
      <button
        type="button"
        onClick={handleCalendarClick}
        className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"
        tabIndex={-1}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </button>
      <input
        ref={hiddenRef}
        type="date"
        value={value}
        onChange={(e) => handlePickerChange(e.target.value)}
        min={min}
        max={max}
        className="absolute inset-0 opacity-0 pointer-events-none"
        tabIndex={-1}
      />
    </div>
  )
}
